import { ServiceBundle } from "channelkart-mobility-bundle/dist/core/serviceBundle";
import store from "../stateManagement/store";
import {Buffer} from 'buffer';
import { EnvironmentRegistry } from "./registry/environmentRegistry";
import axios from "axios";
import { configurationAttributeType, HttpHeaders, NetworkResponse } from "../types";
import { loginData, LoginData, LoginResponse } from "../types/types";
import {
  ChannelkartNetworkPost,
  ChannelkartNetworkPostWithToken,
  ChannelkartNetworkGet,
  ChannelkartNetworkGetWithToken
} from "./networkService";
import { eventEnum, logEvent } from "./eventsService/eventService";
import { openPopup } from "../stateManagement/actions/popupActions";
import { deepClone } from "@mui/x-data-grid/utils/utils";
import { getAuthRefreshToken, getAuthServiceState, logoutTokenAuthService, setAuthServiceAccessExpiry } from "./salescodeAuthService";
import { decryptByte, generateRandomString } from "./encryptionService";
import { dropAllMasterTables } from "./dbService";
import { DMS_CONSTANTS } from "../utils/dmsUtils";
import { getMetaDataConfig } from "./uiConfigService";

const axiosInstance = axios.create({
  withCredentials: true
})

async function signIn(userName: string, password: string, lob: string) {
  const loginData = {
    loginId: userName,
    password: password,
    lob: lob,
    unlimitedExpiry: true,
  };
  let serviceRegistry: ServiceBundle = store.getState().serviceRegistry;
  const networkService = serviceRegistry.get("ChannelkartNetwork") as any;
  return signinServiceEncrypted(loginData) as any;
}

export async function getLoginToken(
  userName: string,
  password: string,
  lob: string
) {
  const loginData: any = {
    loginId: userName,
    password: password,
    lob: lob,
    unlimitedExpiry: true,
  };
  let serviceRegistry: ServiceBundle = store.getState().serviceRegistry;
  const networkService = serviceRegistry.get("ChannelkartNetwork") as any;
  if (networkService.authState.isEndToEndEncryptionEnabled()) {
    const token = await networkService.aesEncryptionService.generateKey();
    loginData.secureToken = token;
    networkService.authState.setHttpEncryptionKey(token);
  }
  const response: NetworkResponse<LoginResponse> =
    await networkService.doSignin(loginData);
  return response.data.token;
}

export function setAuthContext(user: any, token: string) {
  const context: any = {
    user: user,
    env: EnvironmentRegistry.getEnvironment()
  };
  if(localStorage.getItem("refreshToken")==="true"){
    sessionStorage.setItem("access_token",token)
    localStorage.setItem("refreshToken","verified");
  }else{
    localStorage.setItem("access_token", token);
    context.token = token; // to presist previous flow;
  }
  localStorage.setItem("authContext", JSON.stringify(context));
}

export async function login(userName: string, password: string, lob: string) {
  const response = await signIn(userName, password, lob);
  localStorage.setItem("lob",lob);
  return response;
}

export async function sendForgotPasswordOtp(contactAddress: string) {
  const serviceRegistry = store.getState().serviceRegistry;
  const appContextInstace = await serviceRegistry
    .get("ConfigurationService")
    .getConfiguration("appContext");
  appContextInstace.sessionStorageState.otpVerificationFeature =
    "forgotPassword";
  const otpResponse = await serviceRegistry
    .get("AuthenticationService")
    .sendOtpToMobile(contactAddress);
  if (otpResponse.response.statusCode === 200) {
    sessionStorage.setItem(
      "OTP_VERIFY_SESSION_KEY",
      otpResponse.response.sessionId
    );
    sessionStorage.setItem(
      "TEMP_OTP_TOKKEN",
      `Bearer ${otpResponse.response.token}`
    );
  }
  return otpResponse;
}

export async function getPhoneNumberUsedForOtp() {
  const serviceRegistry = store.getState().serviceRegistry;
  const appContextInstace = await serviceRegistry
    .get("ConfigurationService")
    .getConfiguration("appContext");
  return appContextInstace.sessionStorageState.userMobileNumber;
}

export async function setDashboardCookies() {
  let baseUrl = EnvironmentRegistry.getServerUrl();
  if(window.location.hostname.includes("coca-cola.lk")){
    baseUrl = "https://api.coca-cola.lk";
  }else if (window.location.hostname.includes("cokebuddy.in")) {
      baseUrl = "https://api.cokebuddy.in";
  } else if (window.location.hostname.includes("cokebuddy.my")) {
      baseUrl = "https://api.cokebuddy.my";
  } else if (window.location.hostname.includes("cokebuddy.sg")) {
      baseUrl = "https://api.cokebuddy.sg";
  } else if (window.location.hostname.includes("coca-cola.com.mm")) {
      baseUrl = "https://api.coca-cola.com.mm";
  } else if (window.location.hostname.includes("bnl.com.np")) {
      baseUrl = "https://api.bnl.com.np";
  }
  const url = (
    baseUrl +
    "/token/validate");

  const instance = axios.create({ withCredentials: true });
  const cookies: any = [];
  instance.interceptors.response.use((response) => {
    const setCookieHeaders = response.headers["set-cookie"];
    if (setCookieHeaders) {
      setCookieHeaders.forEach((cookie) => {
        cookies.push(cookie);
      });
    }
    return response;
  });
  const headers = {} as { lob: string, Authorization: string };
  headers.lob = getLob();
  const token =  getAccessToken();
  if(token){
    headers.Authorization = token;
  }
  const response = await instance.get(url, { headers});
  return response;
}

export async function signInForUserAndEnv(
  userName: string,
  password: string,
  lob: string,
  env: string
) {
  const loginData: LoginData = {
    loginId: userName,
    password: password,
    lob: lob,
    unlimitedExpiry: true,
  };
  const response: NetworkResponse<LoginResponse> = await signinForEnv(
    loginData,
    env
  );
  return response;
}

async function signinForEnv(input: LoginData, env: string): Promise<any> {
  let network = store.getState().serviceRegistry.get("ChannelkartNetwork");
  if (network.authState.isEndToEndEncryptionEnabled()) {
    const token = await network.aesEncryptionService.generateKey();
    input.secureToken = token;
    network.authState.setHttpEncryptionKey(token);
  }
  const response: NetworkResponse<LoginResponse> = await doSigninForEnv(
    input,
    env
  );
  return response;
}

async function doSigninForEnv(
  input: LoginData,
  env: string
): Promise<NetworkResponse<LoginResponse>> {
  let network = store.getState().serviceRegistry.get("ChannelkartNetwork");
  input.password = await network.encrypt(
    new Date().getTime() + ":" + input.password
  );
  if (input.secureToken) {
    input.secureToken = await network.encrypt(input.secureToken);
  }
  const headers = {} as { lob: string };
  let url = "/signin";
  if (input.lob) {
    headers.lob = input.lob;
    url = url + "?lob=" + input.lob;
  }
  if (input.unlimitedExpiry) {
    url += url.includes("?") ? "&expiry=unlimited" : "?expiry=unlimited";
  }
  url = getFinalUrlForEnv(url, env);
  const response = await network.networkService.post(url, input, headers);
  return response as NetworkResponse<LoginResponse>;
}

function getFinalUrlForEnv(url: string, env: string): string {
  let endUrl = url;
  let baseUrl = EnvironmentRegistry.getUrl(env);
  if (url && !url.startsWith("http") && baseUrl && baseUrl !== "") {
    endUrl = baseUrl + endUrl;
  }
  return endUrl;
}

export async function getLOBClientConfig(Lob?: string) {
  try{
    // 
    const response = await ChannelkartNetworkGet(
      "/metadata/clientconfig",
      undefined,
      Lob ? { Lob } : undefined
    );
    // 
    return response?.data?.features ?? [];
  }catch(error){
    console.error(error);
    return null;
  }
}

export async function getLoginPolicyandTwoFactorAuthState() {
  const lob = localStorage.getItem("lob")
      ? undefined
      : getLob();
  const clientConfig = await getLOBClientConfig(lob);
  const customerConfig = clientConfig?.find(
    (config: { domainType: string }) =>
      config.domainType === "customer_configuration"
  )?.domainValues;
  const showPolicy: boolean =
    customerConfig?.find(
      (configObj: configurationAttributeType) =>
        configObj.name === "showPortalPolicy"
    )?.value ?? false;
  const tncLink: string =
    customerConfig?.find(
      (configObj: configurationAttributeType) =>
        configObj.name === "portalTncLink"
    )?.value ?? "";
  const privacyLink: string =
    customerConfig?.find(
      (configObj: configurationAttributeType) =>
        configObj.name === "portalPrivacyLink"
    )?.value ?? "";
    
  localStorage.setItem("refreshToken","true");
  const isTwoFactorEnabled = clientConfig?.find((config:{domainType:string})=>config.domainType === "authentication_configuration").domainValues?.find((item:{name:string})=>item.name === "portalTwoFactorAuthentication")?.value ?? false;
  const contactType = clientConfig?.find((config:{domainType:string})=>config.domainType === "authentication_configuration").domainValues?.find((item:{name:string})=>item.name === "twoFactorContactType")?.value ?? null;
  const configDomainVals = clientConfig?.find((config:{domainType:string})=>config.domainType === "config")?.domainValues ?? [];
  const loginUIConfig = clientConfig?.find((config:{domainType:string})=>config.domainType === "portal_configuration")?.domainValues?.find((item:{name:string})=>item.name === "loginPageUI")?.value ?? null;
  let isSSOEnabled = false;
  if(configDomainVals.length >0){
    isSSOEnabled = configDomainVals[0]?.["ssoEnable"] ?? false;
  }
  const showPortalLoginFields = clientConfig?.find((config:{domainType:string})=>config.domainType === "authentication_configuration").domainValues?.find((item:{name:string})=>item.name === "showPortalLoginFields")?.value ?? true;
  
  return { policiesObj: {showPolicy, tncLink, privacyLink} , isTwoFactorActive:isTwoFactorEnabled ,isSSOEnabled,showPortalLoginFields , contactType, loginUIConfig};
}

export async function verifyOtpFromServer(
  otp: string,
  contactAddress: string,
  contactAddressType: string,
  twoFactorContactType?:string|null,
  senderId?: string,
  responseId?: string
) {
  let verifyOtp: {[key: string]: any}={} ;
  const portalConfig = await getMetaDataConfig(
    "clientconfig",
    "portal_configuration"
  );
  const portal_config: configurationAttributeType[] =
    portalConfig?.domainValues ?? [];

  const sendIdInForgetPassword =
    portal_config?.find(
      (item) => item.name === "sendIdInForgetPassword"
    )?.value ?? false;

  if(twoFactorContactType){
    verifyOtp={
      otpString:otp
    }
  }else{
    verifyOtp = contactAddressType === "sms"
      ? {
          mobileNumber: contactAddress,
          otpString: otp,
        }
      : {
          email: contactAddress,
          otpString: otp,
        };

  }

  if (senderId) {
    verifyOtp["id"] = senderId;
    verifyOtp["type"] = contactAddressType;
  }
  if (sendIdInForgetPassword && responseId) {
    verifyOtp["id"] = responseId; 
  }

  try {
    const token = sessionStorage.getItem("TEMP_OTP_TOKKEN");
    const url = `/v2/forgotPassword/verify?includeTempToken=true&markUserVerified=true&access_token=${token}`;
    const networkService = store
    .getState()
    .serviceRegistry.get("ChannelkartNetwork");
  
    const response = await  networkService.post(url,  verifyOtp);
    
    if (response.data?.statusCode === 200) {
      sessionStorage.setItem(
        "TEMP_OTP_VERIFY_TOKKEN",
        `Bearer ${response.data?.token}`
      );
    }
    return response;
  } catch (error) {
    return error;
  }
}

export async function setNewPassword(Password: string) {
  const newPasswordData = {
    newPassword: Password,
  };
  try {
    const token: string =
      sessionStorage.getItem("TEMP_OTP_VERIFY_TOKKEN") || "";
    if (token.length === 0) {
      throw new Error("Invalid Token");
    }
    const response = await ChannelkartNetworkPostWithToken(
      "/v1/forgotPassword/changePassword",
      newPasswordData,
      token
    );
    return response;
  } catch (error) {
    return error;
  }
}

export async function getContactAddressType() {
  try {
    const lob = localStorage.getItem("lob")
      ? undefined
      : getLob();
      // 
    const clientConfig = await getLOBClientConfig(lob);
    // 
    const endToend = clientConfig?.find((config: { domainType: string }) => config.domainType === "config")?.domainValues?.[0]?.["endToEndEncryptionEnabled"];
    const signInApiTransformer = clientConfig?.find((config:{domainType:string})=>config.domainType === "portal_configuration")?.domainValues?.find((item:{name:string})=>item.name === "signInApiTransformer")?.value ?? null;
    if (signInApiTransformer) {
      localStorage.setItem("signInApiTransformer", signInApiTransformer);
    }
    if (endToend) {
      localStorage.setItem("endToend", endToend);
    } else {
      localStorage.setItem("endToend", "false");
    }
    const customerConfig = clientConfig.find(
      (config: { domainType: string }) =>
        config.domainType === "authentication_configuration"
    )?.domainValues;
  

    if (!customerConfig) {
      return {contactAddressType :"sms" , contactType:null};
    } else {
      const contactAddressType: string =
        customerConfig?.find(
          (configObj: configurationAttributeType) =>
            configObj.name === "portalForgetPasswordType"
        )?.value ?? "sms";

        const contactType = clientConfig?.find((config:{domainType:string})=>config.domainType === "authentication_configuration").domainValues?.find((item:{name:string})=>item.name === "twoFactorContactType")?.value ?? null;
      return {contactAddressType , contactType};
    }
  } catch (error) {
    return {contactAddressType :"sms" , contactType:null};
  }
}

export async function sendForgotPasswordOtpAddress(
  contactAddress: string,
  contactAddressType: string,
  twoFactorContactType? : string | null,
  optType: "gupshup" | "sinch" = "gupshup"
) {
  const url = `/v1/forgotPassword`;
  const ContactDetails:Record<string,string> = {
    contactAddress: contactAddress,
    type: contactAddressType,
  };

  if (optType === "sinch") {
    ContactDetails["contactType"] = "mobile"
  }

  if(twoFactorContactType !== null && twoFactorContactType !== undefined){
    ContactDetails["contactType"]  = twoFactorContactType;
  }

  const networkService = store
    .getState()
    .serviceRegistry.get("ChannelkartNetwork");
  
  const otpResponse = await  networkService.post(url, ContactDetails);
  if (otpResponse.data.response.statusCode === 200) {
    sessionStorage.setItem(
      "TEMP_OTP_TOKKEN",
      `${otpResponse.data.response.token}`
    );
  }
  return otpResponse.data;
}
export async function signinService(loginData: loginData){
  let serviceRegistry: ServiceBundle = store.getState().serviceRegistry;
  const networkService = serviceRegistry.get("ChannelkartNetwork") as any;
    if (networkService.authState.isEndToEndEncryptionEnabled()) {
      const token = await networkService.aesEncryptionService.generateKey();
      loginData.secureToken = token;
      networkService.authState.setHttpEncryptionKey(token);
  }
  const response = await doSignin(loginData);
  // this.authState.setToken(response.data.token);
  return response;
}

export async function signinServiceEncrypted(loginData: loginData){
  let serviceRegistry: ServiceBundle = store.getState().serviceRegistry;
  const networkService = serviceRegistry.get("ChannelkartNetwork") as any;
  const a =networkService.authState.isEndToEndEncryptionEnabled()
    if (localStorage.getItem("endToend") === "true") {
      const token = await networkService.aesEncryptionService.generateKey();//save and use in network
      loginData.secureToken = token;
      networkService.authState.setHttpEncryptionKey(token);
  }
  const response = await doSignin(loginData);
  // this.authState.setToken(response.data.token);
  return response;
}
export async function doSignin(input: LoginData){
  const networkService = store
    .getState()
    .serviceRegistry.get("ChannelkartNetwork");
    const random = generateRandomString(8)
    if(localStorage.getItem("endToend") === "true"){
      input.password = await networkService.encrypt( new Date().getTime() + "~" + random + ":" + input.password);
    }else{
      input.password = await networkService.encrypt(new Date().getTime() + ":" + input.password);
    }
  if (input.secureToken) {
    if(localStorage.getItem("endToend") === "true"){
      input.secureToken = await networkService.encrypt(random+"~"+input.secureToken);
    }else{
      input.secureToken = await networkService.encrypt(input.secureToken);
    }
  }
  const headers = {} as { lob: string };
  let url = "/signin";
  if (input.lob) {
      headers.lob = input.lob;
      url = url + "?lob=" + input.lob;
  }
  if (input.unlimitedExpiry) {
      url += url.includes("?") ? "&expiry=unlimited" : "?expiry=unlimited";
  }
  const signInApiTransformer = localStorage.getItem("signInApiTransformer");
  if(signInApiTransformer && signInApiTransformer?.trim()?.length > 0) url+= `&transformerOut=${signInApiTransformer}`;
  url += "&source=portal";
  // const config = { withCredentials: true }
  // 
  const response = await axiosPost(url, input, headers);
  const token = await networkService.authState.getHttpEncryptionKey();
  // console.log("token",token)
  // 
  if(localStorage.getItem("endToend") === "true"){
    // const decryptedResponseNetwork=await networkService.aesEncryptionService.decryptByte(response.data,token)
    const decryptedResponse=await decryptByte(new Uint8Array(response.data), new Uint8Array(Buffer.from(token, 'base64')));
    const parsedData = JSON.parse(decryptedResponse);
    return {
      ...response,
      data: parsedData
    };
  }else{
    return response ;
  }
}

export async function axiosPost(url: string, input: LoginData, headers?: HttpHeaders,config?: any){
  const networkService = store
    .getState()
    .serviceRegistry.get("ChannelkartNetwork");
  url = networkService.getFinalUrl(url);
  const configObj = { ...config, headers: headers };
  const token = await networkService.authState.getHttpEncryptionKey();
  if (localStorage.getItem("endToend") === "true" && token) {
    configObj.responseType = 'arraybuffer';
  }
  
  return axios.post(url, input, configObj);
}
export async function axiosGet(url: string, headers?: HttpHeaders,config?: any){
  const networkService = store
  .getState()
  .serviceRegistry.get("ChannelkartNetwork");
  url = networkService.getFinalUrl(url);
  const configObj = {...config, headers: headers };
  return axiosInstance.get(url,configObj);
}
export async function getRefreshToken(){
  const headers = {lob: getLob() ?? ""}
  const response = await axiosGet("/token/refresh?source=portal",headers);
  if(response.status === 200){
    return response.data;
  }else if(response.status === 403){
    throw new Error("Session Expired")
  }else{ // internal server error
    throw new Error("You have been logged out due to security reasons")
  }
}
export async function verifyAndRefreshUserToken(){
  try{
      if(sessionStorage.getItem("access_token")){
          return true; //is logged in
      }else if(!sessionStorage.getItem("access_token") && localStorage.getItem("refreshToken") === "verified"){
          const isAuthServiceEnabled = await getAuthServiceState();
          if(isAuthServiceEnabled){
            const response = await getAuthRefreshToken();
            if(response?.accessToken){
              sessionStorage.setItem("access_token", response.accessToken);
              setAuthServiceAccessExpiry(response.accessTokenExpiresIn);
            return true;
            }
            else{
              throw new Error("You have been logged out due to security reasons")
            }
          }
          const response = await getRefreshToken();
          const token = response?.token;
          if(token){
            sessionStorage.setItem("access_token",token);
            return true;
          }else{
            throw new Error("You have been logged out due to security reasons")
          }
      }else if(!localStorage.getItem("refreshToken") && localStorage.getItem("access_token")){
          return true;
      }else{
          throw new Error("You have been logged out due to security reasons");
      }
  }catch(err: any){
      return false;
  }
}

export async  function logoutUser(setFlag?: boolean, setAuthFlag?:boolean){
  if(setAuthFlag){
    await logoutAuthService(setAuthFlag);
    }
    await logoutToken(setAuthFlag);
    if(setFlag || setAuthFlag){
        sessionStorage.setItem("accessDenied","true");
    }
    localStorage.removeItem("access_token");
    if(localStorage.getItem("refreshToken")){
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("access_token");
    }
    localStorage.removeItem("lob");
    localStorage.removeItem("authContext");
    localStorage.removeItem("compareLobName")
    localStorage.removeItem("compareLobAccessToken");
    // 
    if(localStorage.getItem("endToend")){
      localStorage.removeItem("endToend");
    }
    if(localStorage.getItem("lastDataSyncTime")) {
      localStorage.removeItem("lastDataSyncTime");
    }
    if(localStorage.getItem("multipleFiltersListManagePrimaryOMS")) {
      localStorage.removeItem("multipleFiltersListManagePrimaryOMS");
    }
    if(localStorage.getItem(DMS_CONSTANTS.MULTIPLE_FILTERS_LIST_MANAGE_SECONDARY_ORDER_INVOICE)) {
      localStorage.removeItem(DMS_CONSTANTS.MULTIPLE_FILTERS_LIST_MANAGE_SECONDARY_ORDER_INVOICE);
    }
    if(localStorage.getItem(DMS_CONSTANTS.CLAIMS)) {
      localStorage.removeItem(DMS_CONSTANTS.CLAIMS);
    }
    if(localStorage.getItem("multipleFiltersListManageInvoice")) {
      localStorage.removeItem("multipleFiltersListManageInvoice");
    }
    if(localStorage.getItem("ConfigDictionary")) {
      localStorage.removeItem("ConfigDictionary");
    }
    if(localStorage.getItem("multipleFiltersListManageOrders")) {
      localStorage.removeItem("multipleFiltersListManageOrders");
    }
    if(localStorage.getItem("multipleFiltersListBulkInvoice")){
      localStorage.removeItem("multipleFiltersListBulkInvoice");
    }

    await dropAllMasterTables();
    window.location.replace("/dashboardLogin")
  
}

async function logoutAuthService(setFlag:boolean){
  const token = getAccessToken() ?? "";
  const response = await logoutTokenAuthService(token, setFlag);
  return response;

}
export function getAccessToken(){
  return (localStorage.getItem("refreshToken") ? sessionStorage.getItem("access_token") : localStorage.getItem("access_token"));
}
export async function getAndSetRefreshTokenConfig(){
  const lob = localStorage.getItem("lob")
      ? undefined
      : getLob();
  localStorage.setItem("refreshToken","true");
}
export function setAuthContextInStorage(context:any){
  const contextClone = deepClone(context);
  if(localStorage.getItem("refreshToken")){
    sessionStorage.setItem("access_token",context.token);
    Reflect.deleteProperty(contextClone, 'access_token');
  }else{
    localStorage.setItem("access_token",context.token);
  }
  localStorage.setItem("lob",context?.user?.lob);
  localStorage.setItem("authContext", JSON.stringify(contextClone));
}
export async function logoutToken(setAuthFlag?:boolean){
  if(!setAuthFlag){
  const isAuthServiceEnabled = await getAuthServiceState();
  const token = getAccessToken() ?? "";
    const AUTH_TOKEN_PREFIX = Buffer.from('auth').toString('base64');
    if(isAuthServiceEnabled.isAuthService && token.startsWith(AUTH_TOKEN_PREFIX)){
    const response = await logoutTokenAuthService(token);
    return response;
  }
  const url = "/logoff?source=portal"
  const lob = localStorage.getItem("lob") ?? getLob();
  const headers = {
    Lob: lob ?? "",
    Authorization: getAccessToken() ?? "",
  }
  const response = await axiosGet(url,headers);
  return response;
}
}


export function getUserDetailsFromContext(keyName?:string){
  const user =  JSON.parse(localStorage.getItem('authContext') ?? "")?.user ?? {}
  if(!keyName) return user ?? {};
  

  return user[keyName];
}


export function validateContactAddress(addressType:string , address:string | null):{isValid:boolean , message:string}{
  
  let res = {
    isValid:true,
    message:"Succcess"
  }
  
  if(addressType.toLocaleLowerCase() === "sms"){
    if(!address) {
      res = {isValid :false , message:'Invalid Mobile number'};
    }
  }
  else if(addressType.toLocaleLowerCase() === "email"){
    if(!address || !validateEmail(address)){
      res = {isValid :false , message:'Invalid Email address'};
    }
  }else if(addressType.toLocaleLowerCase() === "loginid"){
    if(!address || address.trim().length === 0){
      res = {isValid :false , message:'Invalid login id'};
    }
  }


  return res;
}


const validateEmail = (email:string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};


export async function getUserFromToken(token:string){
  const headers = EnvironmentRegistry.makeAuthHeaders(token);
  const response = await ChannelkartNetworkGet("/login/user",null,headers);
  return response.data;
}

export const getMobileKeyName = (user:Record<string , any>)=>{
  const keyPriorities = ["mobile","registeredNumber"];
  for(let key of keyPriorities){
    if(user[key] && user[key].trim() !== ""){
      return key;
    }
  }

  return "mobile";
}

export function getLob(){
  const domain = window.location.hostname;
  if(localStorage.getItem("lob")){
      return localStorage.getItem("lob")!;
  }else if (domain == "cokebuddy.coca-cola.com.mm") {
      return "cokemm"
  } else if (domain == "cokebuddy.bnl.com.np") {
      return "cokenp";
  } else if(domain=="cokebuddy.coca-cola.lk"){
      return "cokeslk";
  }else if(domain=="alingcora.ccbp.com.ph"){
    return "cokepheb2b";
  }else if(domain=="sfa-aiph.salescode.ai"){
    return "jnjaiph";
  } else if (domain == "comondo-morocco.eccbc.com") {
    return "cokemrc";
  } else if (domain == "scprdportal.alsafidanone.com.sa" || domain == "scodeprdcdn.alsafidanone.com.sa" || domain == "asd-scode-cdn-function-xnieo7ff6a-wx.a.run.app") {
    return "alsafisa";
  } else {
      const domainParts = domain.split(".");
      if(domainParts[0]=="www"){
          if (domainParts[1] == "cokebuddy" && (domainParts[2] == "my")) {
              return "simamy";
          }
          else if (domainParts[1] == "cokebuddy" && (domainParts[2] == "sg")) {
              return "simasg";
          }else if (domainParts[1] == "cokebuddy" && (domainParts[2] == "in")) {
            return "kbuddy";
        }else{
              return domainParts[1];
          }
      }else {
          return domainParts[0] ?? "";
      }
  }
}
export async function getLoginUIConfig() {
  const lob = localStorage.getItem("lob")
  ? undefined
  : getLob();
  const clientConfig = await getLOBClientConfig(lob);
  const loginUIConfig = clientConfig?.find((config:{domainType:string})=>config.domainType === "portal_configuration")?.domainValues?.find((item:{name:string})=>item.name === "loginPageUI")?.value ?? null;
  return {loginUIConfig};
}
